import React, { useState, useEffect } from 'react';
import { Chart } from "react-google-charts";
import { Drawer, CloseButton, Button } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import CampaignReport from "../../api/CampaignReport";
import AggregatedReportsAPI from "../../api/AggregatedReportsApi";
import { VisiLoader, useColorScheme } from "../../components/GeneralComponent/GeneralComponent";

export function ReportVelocityDrawer({ report_id, showVelocityDrawer, setShowVelocityDrawer, campaignGoalId, encodeCampaignName, selectedCampaignName, adsetName, trafficSource, campaign_id, trafficLevel, goalType, combinedGoalType, selectedAttributionModal, displayCampaignName, velocityMode = 'traffic', lifecycleVelocityParams = {}, aggregateVelocityParams = {}}) {
  
  const navigate = useNavigate();
  const colorTheme = useColorScheme();
  const isDark = colorTheme === 'dark';

  const [isVisiLoaderVisible, setIsVisiLoaderVisible] = useState(false);
  // The aggregate report shows every number with two decimals; the campaign report keeps its own format.
  const twoDp = (v: any, other: any) => (velocityMode === 'aggregate' ? Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : other);
  const [campaignName, setCampaignName] = useState('');
  const [goalName, setGoalName] = useState('');
  const [velocityData, setVelocityData] = useState([]);
  const [downstreamGoalVelocityData, setDownstreamGoalVelocityData] = useState([]);
  const [velocityChartData, setVelocityChartData] = useState([]);
  const [conversionsAfterDay7, setConversionsAfterDay7] = useState('');
  const [conversionsAfterDay30, setConversionsAfterDay30] = useState('');
  const [averageTimeConversion, setAverageTimeConversion] = useState('');
  const [medianTimeConversion, setMedianTimeConversion] = useState('');
  const [downstreamGoals, setDownstreamGoals] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState({});
  const [downstreamResultsTable, setDownstreamResultsTable] = useState([]);
  const [downstreamGoalDropMsg, setDownstreamGoalDropMsg] = useState('');
  const [formattedTime, setFormattedTime] = useState('');
  const velocityColors = {
    'Day 0' : '#4c6ef5',
    'Day 1' : '#5c7cfa',
    'Days 2–7' : '#748ffc',
    'Days 8–14' : '#91a7ff',
    'Days 15–30' : '#a5b4fc',
    'Days 31–60' : '#bac8ff',
    'Days 61–90' : '#d0d9ff',
    'Days 91+' : '#e5dbff',
    'Non-converters' : '#f3f0ff'
  };

  const handleGoalToggle = (goalId) => {
    setSelectedGoals(prev => {
      const updated = { ...prev };
      if (updated[goalId] !== undefined) {
        delete updated[goalId];
        return Object.fromEntries(
          Object.entries(updated)
            .sort((a, b) => Number(a[1]) - Number(b[1]))
            .map(([id], index) => [id, index])
        );
      }
      updated[goalId] = Object.keys(updated).length;
      return updated;
    });
  };

  const handleclose = () => {
    setShowVelocityDrawer(false);
  }

  const getVelocityDrawerData = () => {
    setIsVisiLoaderVisible(true);
    let apiPromise;
    if (velocityMode === 'lifecycle') {
      apiPromise = CampaignReport.getlifecyclevelocitydrawerdata(lifecycleVelocityParams);
    } else if (velocityMode === 'aggregate') {
      const { velocity_mode, ...aggregatePayload } = (aggregateVelocityParams || {}) as Record<string, any>;
      apiPromise = velocity_mode === 'goal'
        ? AggregatedReportsAPI.getlifecyclevelocitydrawerdata(aggregatePayload)
        : AggregatedReportsAPI.getvelocitydrawerdata(aggregatePayload);
    } else {
      let dataObject = {
        report_id: report_id,
        traffic_source: trafficSource,
        campaign_goal_id: campaignGoalId,
        encode_campaign: encodeCampaignName,
        campaign_id: campaign_id,
        traffic_level: trafficLevel,
        goal_type: goalType,
        combined_goal: combinedGoalType
      } as any;
      if(trafficLevel === 'adset'){
        dataObject.campaign_name = selectedCampaignName;
      }
      if(trafficLevel === 'ads'){
        dataObject.campaign_name = selectedCampaignName;
        dataObject.adset_name = adsetName;
      }
      apiPromise = CampaignReport.getvelocitydrawerdata(dataObject);
    }
    apiPromise
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
          setIsVisiLoaderVisible(false);
        }
      }
    })
    .then(data => {
      if(data.status === 1){
        setCampaignName(data.data.campaign_name);
        setGoalName(data.data.goal_name);
        setVelocityData(data.data.breakdown_table);
        setDownstreamGoalVelocityData(data.data.downstream_bucket_results);
        setDownstreamGoals(data.data.downstream_goal_drop);
        setConversionsAfterDay7(data.data.conversions_after_7);
        setConversionsAfterDay30(data.data.conversions_after_30);
        setAverageTimeConversion(data.data.average_time_conversion);
        setMedianTimeConversion(data.data.median_time_conversion);
        setDownstreamGoalDropMsg(data.data.downstream_goal_drop_message);
        setFormattedTime(data.data.formatted_time);
        let chartData = [
          ["Bucket", "Conversions", { role: "style" }, { role: "tooltip", type: "string", p: { html: true } }]
        ];
        data.data.breakdown_table.forEach((row) => {
          if (row.bucket === "Non-converters") return;
          chartData.push([
            row.bucket,
            row.conversions,
            `color: ${velocityColors[row.bucket]}; stroke-width:2;`,
            `<div style="padding:6px;">
                <b>${row.bucket}</b><br>
                Conversions: ${row.conversions}<br>
                Revenue: ${twoDp(row.revenue, row.revenue)}<br>
                ${twoDp(row.percentage, row.percentage)}% of total
             </div>`
          ]);
        });
        setVelocityChartData(chartData);
        const selectedDownstreamGoals = data.data.downstream_goal_drop
          .filter(goal => goal.selected)
          .reduce((acc, goal) => ({ ...acc, [goal.goal_id]: goal.index }), {});
        setSelectedGoals(selectedDownstreamGoals);
        setIsVisiLoaderVisible(false);
      }
    })
    .catch(error => {
      console.error("API Error:", error);
      setIsVisiLoaderVisible(false);
    })
  };

  useEffect(() => {
    const downstreamRows = getDownstreamResults(
      velocityData,
      downstreamGoalVelocityData,
      selectedGoals
    );
    setDownstreamResultsTable(downstreamRows);
  }, [selectedGoals]);

  useEffect(() => {
    if(showVelocityDrawer){
      getVelocityDrawerData();
    }
  }, [showVelocityDrawer]);

  function getDownstreamResults(velocityData, downstreamGoalVelocityData, selectedGoals) {
    const goalOrder = Object.entries(selectedGoals)
      .sort((a, b) => Number(a[1]) - Number(b[1]))
      .map(([goalId]) => goalId);

    if (goalOrder.length === 0) return [];
    const bucketOrder = velocityData.map(row => row.bucket);
    const finalRows = calculateDownstreamTable(velocityData, goalOrder, bucketOrder);
    return finalRows;
  }

  function calculateDownstreamTable(primaryBuckets, goalOrder, bucketOrder) {
    return bucketOrder.map((bucket) => {
      let totalRevenue = 0;
      let totalCurrentConversions = 0;

      goalOrder.forEach((goalId, index) => {
        let currentRevenue = downstreamGoalVelocityData[goalId][bucket]?.revenue || 0;
        let currentConv = downstreamGoalVelocityData[goalId][bucket]?.conversions || 0;

        totalCurrentConversions += currentConv;
        totalRevenue += currentRevenue;
      });
      const primaryGoalConv = primaryBuckets.find((d) => d.bucket === bucket)?.conversions || 0;
      const completionRate = bucket === 'Non-converters' ? 0 : primaryGoalConv > 0 ? (totalCurrentConversions / primaryGoalConv) * 100 : 0;

      return {
        bucket,
        completionRate: Number(completionRate.toFixed(velocityMode === 'aggregate' ? 2 : 1)),
        revenue: Number(totalRevenue.toFixed(2)),
      };
    });
  }

  const exportToCSV = () => {
    let csvRows = [];
    const selectedGoalIds = Object.keys(selectedGoals);  
    const selectedGoalNames = selectedGoalIds.map(id => {
      const found = downstreamGoals.find(g => g.goal_id === id);
      return found ? found.goal_name : "";
    });
    const namesString = selectedGoalNames.join(", ");
    csvRows.push(`Selected Goal: ${goalName}`);
    csvRows.push(`Downstream Goals: ${namesString}`);
    csvRows.push(`Date Range: ${formattedTime}`);
    csvRows.push(`Attribution Model: ${selectedAttributionModal}`);
    csvRows.push(`Segment: ${campaignName}`);
    csvRows.push("");
    csvRows.push("Velocity Breakdown Table");
    csvRows.push("Time Bucket,Conversions,% of Total,Revenue,AOV,Cumulative %");
    let totalConversions = 0;
    let totalRevenue = 0;
    velocityData.forEach(row => {
      if(row.bucket !== 'Non-converters'){
        totalConversions += row.conversions;
        totalRevenue += row.revenue;
      }
      const hasCumulative = row.cumulative !== "—" && row.cumulative !== "-" && !isNaN(Number(row.cumulative));
      csvRows.push([
        row.bucket,
        row.conversions.toLocaleString(),
        `${velocityMode === 'aggregate' ? Number(row.percentage).toFixed(2) : row.percentage}%`,
        velocityMode === 'aggregate' ? Number(row.revenue).toFixed(2) : row.revenue.toLocaleString(),
        row.conversions > 0 ? (row.revenue / row.conversions).toFixed(2) : "0.00",
        hasCumulative ? `${row.cumulative}%` : '—'
      ].join(","));
    });
    const totalAOV = totalConversions > 0  ? (totalRevenue / totalConversions).toFixed(2) : 0;
    csvRows.push(velocityMode === 'aggregate'
      ? `Totals,${totalConversions},100.00%,${totalRevenue.toFixed(2)},${Number(totalAOV).toFixed(2)},100.00%`
      : `Totals,${totalConversions.toLocaleString()},100%,${totalRevenue.toLocaleString()},${totalAOV},100%`);

    csvRows.push("");
    csvRows.push("Downstream Goal Analysis");
    csvRows.push("Time Bucket,Completion Rate,Downstream Revenue");
    downstreamResultsTable.forEach(row => {
      csvRows.push([
        row.bucket,
        `${velocityMode === 'aggregate' ? Number(row.completionRate).toFixed(2) : row.completionRate}%`,
        velocityMode === 'aggregate' ? Number(row.revenue).toFixed(2) : row.revenue
      ].join(","));
    });

    csvRows.push("");
    csvRows.push("Summary Metrics");
    csvRows.push("Metric,Value");
    csvRows.push(`Median Time to Conversion,${medianTimeConversion}`);
    csvRows.push(`Average Time to Conversion,${averageTimeConversion}`);
    csvRows.push(`Conversions After Day 7,${conversionsAfterDay7}`);
    csvRows.push(`Conversions After Day 30,${conversionsAfterDay30}`);

    const safeGoal = goalName.toLowerCase().replace(/\s+/g, "_");
    const safeCampaign = campaignName.toLowerCase().replace(/\s+/g, "_");
    const todayDate = new Date().toISOString().split("T")[0];
    const fileName = `velocity_${safeGoal}_${safeCampaign}_${todayDate}`;
    const csvString = csvRows.join("\n");
    const blob = new Blob(["\ufeff" + csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const drawerSurfaceColor = isDark ? '#1a1b1e' : 'white';
  const tableRowBorderColor = isDark ? '#373a40' : '#f1f3f5';
  const tableHeadBorderColor = isDark ? '#4c4f57' : '#e9ecef';
  const tableHeaderTextColor = isDark ? '#e9ecef' : '#495057';
  const tableCellTextColor = isDark ? '#dee2e6' : '#212529';
  const badgeBlueBackground = isDark ? '#274a8a' : '#e7f5ff';
  const badgeBlueText = isDark ? '#dbe4ff' : '#1971c2';
  const badgeGreenBackground = isDark ? '#2b5a38' : '#d3f9d8';
  const badgeGreenText = isDark ? '#d3f9d8' : '#2b8a3e';
  const cumulativeBarBackground = isDark ? '#3b3f46' : '#e9ecef';
  const cumulativeTextColor = isDark ? '#adb5bd' : '#868e96';

  return (
    <Drawer opened={showVelocityDrawer} onClose={handleclose} position="right" size="xl" withCloseButton={false} zIndex={velocityMode === 'lifecycle' ? 1200 : undefined}>
        <div style={{ position: 'relative', minHeight: '100vh' }}>
          <VisiLoader VisiLoaderVisible={isVisiLoaderVisible}/>
          <div style={{ padding: '24px', borderBottom: `1px solid ${colorTheme === 'dark' ? '#373A40' : '#e9ecef'}`, position: 'sticky', top: 0, backgroundColor: colorTheme === 'dark' ? '#25262B' : 'white', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '600' }}>Velocity – {goalName}</h2>
              <p style={{ margin: 0, fontSize: '14px', color: colorTheme === 'dark' ? '#909296' : '#868e96' }}>{displayCampaignName || campaignName}</p>
            </div>
            <Button disabled={isVisiLoaderVisible} style={{  marginTop: '5px', marginLeft: '150px' }} onClick={exportToCSV}>Export Velocity Data</Button>
            <button
              onClick={handleclose}
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: "6px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "#495057",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f3f5")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <CloseButton onClick={handleclose} size="lg" variant="subtle" />
            </button>
          </div>
          <div style={{ backgroundColor: colorTheme === 'dark' ? '#25262B' : 'white', border: `1px solid ${colorTheme === 'dark' ? '#373A40' : '#e9ecef'}`, borderRadius: '8px', marginBottom: '24px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>Conversion Velocity</h3>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>{`(Time from First Click → ${goalName})`}</h3>
            <div style={{ marginTop: '8px' }}>
              <Chart
                chartType="ColumnChart"
                width="100%"
                height="420px"
                data={velocityChartData}
                options={{
                  legend: { position: "none" },
                  bar: { groupWidth: "55%" },
                  animation: {
                    startup: true,
                    duration: 800,
                    easing: "easeOut",
                  },
                  backgroundColor: "transparent",
                  tooltip: { isHtml: true },
                  chartArea: { width: "85%", height: "75%" },
                  hAxis: {
                    textStyle: { fontSize: 11, color: colorTheme === 'dark' ? '#909296' : '#000000' },
                    slantedText: true,
                    slantedTextAngle: 30
                  },
                  vAxis: {
                    minValue: 0,
                    gridlines: { color: colorTheme === 'dark' ? '#373A40' : '#EEEEEE' },
                    textStyle: { fontSize: 12, color: colorTheme === 'dark' ? '#909296' : '#000000' },
                  },
                }}
              />
            </div>
          </div>
          {/* Velocity Breakdown Table */}
          <div style={{ backgroundColor: drawerSurfaceColor, border: `1px solid ${tableHeadBorderColor}`, borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Velocity Breakdown</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${tableHeadBorderColor}` }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Time Bucket</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Conversions</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>% of Total</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Revenue</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>AOV</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Cumulative %</th>
                  </tr>
                </thead>
                <tbody>
                  {velocityData.map((row, index) => {
                    const hasCumulative = row.cumulative !== "—" && row.cumulative !== "-" && !isNaN(Number(row.cumulative));
                    return(
                      <tr key={row.bucket} style={{ borderBottom: `1px solid ${tableRowBorderColor}` }}>
                        <td style={{ padding: '12px 8px', color: tableCellTextColor }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: velocityColors[row.bucket] }} />
                            <span style={{ fontSize: '13px', fontWeight: '500', color: tableCellTextColor }}>{row.bucket}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', color: tableCellTextColor }}>
                          {row.conversions.toLocaleString()}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          <span style={{ backgroundColor: badgeBlueBackground, color: badgeBlueText, padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500'}}>
                            {twoDp(row.percentage, row.percentage)}%
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', color: tableCellTextColor }}>
                          {twoDp(row.revenue, row.revenue.toLocaleString())}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', color: tableCellTextColor }}>
                          {row.conversions > 0 ? (row.revenue / row.conversions).toFixed(2) : '0.00'}
                        </td>
                        {hasCumulative ? (
                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                              <div style={{ width: '60px', height: '6px', backgroundColor: cumulativeBarBackground, borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ width: `${row.cumulative}%`, height: '100%', backgroundColor: '#4c6ef5', transition: 'width 0.3s ease' }} />
                              </div>
                              <span style={{ fontSize: '12px', color: cumulativeTextColor, minWidth: '45px' }}>
                                {hasCumulative ? `${row.cumulative}%` : '—'}
                              </span>
                            </div>
                          </td>
                        ) : (
                           <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{ fontSize: '12px', color: cumulativeTextColor, minWidth: '45px' }}>
                              {'—'}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* Downstream Goals */}
          <div style={{ backgroundColor: drawerSurfaceColor, border: `1px solid ${tableHeadBorderColor}`, borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>Downstream Goal Analysis</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: colorTheme === 'dark' ? '#909296' : '#868e96' }}>
              Select downstream goals to see cohort quality by velocity bucket
            </p>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {downstreamGoals.length > 0 ? (
                downstreamGoals.map(goal => (
                  <label key={goal.goal_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={goal.goal_id in selectedGoals}
                      onChange={() => handleGoalToggle(goal.goal_id)}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        accentColor: '#4c6ef5'
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>{goal.goal_name}</span>
                  </label>
                ))
              ) : (
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                  {downstreamGoalDropMsg}
                </h4>
              )}
            </div>
            {downstreamResultsTable.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${tableHeadBorderColor}` }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Time Bucket</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Completion Rate</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: tableHeaderTextColor }}>Downstream Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {downstreamResultsTable.map((row) => (
                      <tr key={row.bucket} style={{ borderBottom: `1px solid ${tableRowBorderColor}` }}>
                        <td style={{ padding: '12px 8px', fontSize: '13px', color: tableCellTextColor }}>{row.bucket}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          <span style={{
                            backgroundColor: badgeGreenBackground,
                            color: badgeGreenText,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {twoDp(row.completionRate, row.completionRate)}%
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', color: tableCellTextColor }}>
                          {twoDp(row.revenue, row.revenue.toLocaleString())}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div style={{ backgroundColor: colorTheme === 'dark' ? '#25262B' : 'white', border: `1px solid ${colorTheme === 'dark' ? '#373A40' : '#e9ecef'}`, borderRadius: '8px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Summary Metrics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', color: colorTheme === 'dark' ? '#909296' : '#868e96', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Median Time to Conversion
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: colorTheme === "light" ? '#212529' : '#becad6' }}>{medianTimeConversion}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: colorTheme === 'dark' ? '#909296' : '#868e96', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Average Time to Conversion
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: colorTheme === "light" ? '#212529' : '#becad6' }}>{averageTimeConversion}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: colorTheme === 'dark' ? '#909296' : '#868e96', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Conversions After Day 7
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: colorTheme === "light" ? '#212529' : '#becad6' }}>{conversionsAfterDay7}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: colorTheme === 'dark' ? '#909296' : '#868e96', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Conversions After Day 30
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: colorTheme === "light" ? '#212529' : '#becad6' }}>{conversionsAfterDay30}</div>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
  );
}